import { useState } from 'react';
import { useMutation } from '@/apollo';
import { CREATE_APPOINTMENT_DETAIL, UPDATE_APPOINTMENT_DETAIL, DELETE_APPOINTMENT_DETAIL } from '../../../graphql/appointment-details';
import type { AppointmentDetail, AppointmentDetailFormState } from '../types/moderator.types';
import { initialAppointmentDetailState } from '../types/moderator.types';
import { logError } from '../../../utils/errorMessages';

export interface UseAppointmentDetailManagementReturn {
  // State
  showForm: boolean;
  editing: AppointmentDetail | null;
  form: AppointmentDetailFormState;
  searchTerm: string;
  newNote: string;
  showDeleteModal: boolean;
  deleteId: string | null;
  
  // State setters
  setShowForm: (show: boolean) => void;
  setEditing: (detail: AppointmentDetail | null) => void;
  setForm: (form: AppointmentDetailFormState) => void;
  setSearchTerm: (term: string) => void;
  setNewNote: (note: string) => void;
  
  // Handlers
  handleCreate: (customerId?: string) => void;
  handleEdit: (detail: AppointmentDetail) => void;
  handleDelete: (id: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCancel: () => void;
  handleAddNote: () => void;
  handleRemoveNote: (index: number) => void;
  handleReorderNotes: (notes: string[]) => void;
  
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useAppointmentDetailManagement = (
  refetchAppointmentDetails: () => void
): UseAppointmentDetailManagementReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AppointmentDetail | null>(null);
  const [form, setForm] = useState<AppointmentDetailFormState>(initialAppointmentDetailState);
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [createAppointmentDetail, { loading: isCreating }] = useMutation(CREATE_APPOINTMENT_DETAIL, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialAppointmentDetailState);
      refetchAppointmentDetails();
    },
    onError: (error) => {
      logError(error, 'Create Appointment Detail');
    }
  });

  const [updateAppointmentDetail, { loading: isUpdating }] = useMutation(UPDATE_APPOINTMENT_DETAIL, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialAppointmentDetailState);
      refetchAppointmentDetails();
    },
    onError: (error) => {
      logError(error, 'Update Appointment Detail');
    }
  });

  const [deleteAppointmentDetail, { loading: isDeleting }] = useMutation(DELETE_APPOINTMENT_DETAIL, {
    onCompleted: () => {
      setShowDeleteModal(false);
      setDeleteId(null);
      refetchAppointmentDetails();
    },
    onError: (error) => {
      logError(error, 'Delete Appointment Detail');
    }
  });

  const handleCreate = (customerId?: string) => {
    setShowForm(true);
    setEditing(null);
    setForm({ ...initialAppointmentDetailState, customerId: customerId || '' });
  };

  const handleEdit = (detail: AppointmentDetail) => {
    setShowForm(true);
    setEditing(detail);
    
    // Handle migration from boolean to string values
    let canCancel = detail.canCancel || 'Yes';
    let canReschedule = detail.canReschedule || 'Yes';
    let canSchedule = detail.canSchedule || 'No';
    
    // Convert legacy boolean values to strings if needed
    if (typeof canCancel === 'boolean') {
      canCancel = canCancel ? 'Yes' : 'No';
    }
    if (typeof canReschedule === 'boolean') {
      canReschedule = canReschedule ? 'Yes' : 'No';
    }
    if (typeof canSchedule === 'boolean') {
      canSchedule = canSchedule ? 'Yes' : 'No';
    }
    
    setForm({
      name: detail.name,
      canCancel: canCancel as string,
      canReschedule: canReschedule as string,
      canSchedule: canSchedule as string,
      customerId: detail.customerId || '',
      notes: detail.notes?.map(n => n.content) || []
    });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteAppointmentDetail({ variables: { id: deleteId } });
        // Modal will be closed in onCompleted callback
      } catch (error) {
        // Error is already logged in onError callback
        // Don't close modal on error - let user retry or cancel
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure canCancel, canReschedule, and canSchedule are strings, not booleans
    const input = {
      ...form,
      canCancel: typeof form.canCancel === 'string' ? form.canCancel : (form.canCancel ? 'Yes' : 'No'),
      canReschedule: typeof form.canReschedule === 'string' ? form.canReschedule : (form.canReschedule ? 'Yes' : 'No'),
      canSchedule: typeof form.canSchedule === 'string' ? form.canSchedule : (form.canSchedule ? 'Yes' : 'No')
    };
    
    if (editing) {
      await updateAppointmentDetail({
        variables: {
          id: editing.id,
          input
        }
      });
    } else {
      await createAppointmentDetail({
        variables: {
          input
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(initialAppointmentDetailState);
    setNewNote('');
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setForm({ ...form, notes: [...form.notes, newNote.trim()] });
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setForm({ ...form, notes: form.notes.filter((_, i) => i !== index) });
  };

  const handleReorderNotes = (notes: string[]) => {
    setForm({ ...form, notes });
  };

  return {
    showForm,
    editing,
    form,
    searchTerm,
    newNote,
    showDeleteModal,
    deleteId,
    setShowForm,
    setEditing,
    setForm,
    setSearchTerm,
    setNewNote,
    handleCreate,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleSubmit,
    handleCancel,
    handleAddNote,
    handleRemoveNote,
    handleReorderNotes,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

